import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
} from 'firebase/auth';
import { ToastContainer, toast } from 'react-toastify';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  confirmNativePhoneCode,
  startNativePhoneVerification,
} from '../nativePhoneAuth';
import {
  clearCurrentUserProfile,
  getPostVerificationRoute,
  loadUserProfile,
  persistCurrentUserProfile,
} from '../utils/authVerification';
import RegistrationProgress from '../components/RegistrationProgress';

const toE164 = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (/^\+\d{7,15}$/.test(raw)) return raw;
  return null;
};

const syncStoredPhoneVerification = (phoneValue) => {
  try {
    const existing = JSON.parse(localStorage.getItem('currentUserProfile') || '{}');
    if (!existing || typeof existing !== 'object') return;
    localStorage.setItem(
      'currentUserProfile',
      JSON.stringify({
        ...existing,
        phoneVerified: true,
        phoneNumber: phoneValue || existing.phoneNumber || '',
      })
    );
  } catch (_) {}
};

const RECAPTCHA_CONTAINER_ID = 'coldwire-phone-recaptcha';

const getRecaptchaContainer = () => {
  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    container.style.minHeight = '78px';
    container.style.marginBottom = '12px';
    document.body.appendChild(container);
  }
  return container;
};

function WebPhoneVerify({ phone: propPhone, onVerified, onCancel, isOpen = false }) {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [phone, setPhone] = useState(propPhone || '');
  const [linkMode, setLinkMode] = useState(false);
  const [verificationBlocked, setVerificationBlocked] = useState(false);
  const timerRef = useRef(null);
  const verifierRef = useRef(null);
  const renderPromiseRef = useRef(null);
  const sendInFlightRef = useRef(false);

  useEffect(() => {
    const init = async () => {
      try {
        if (propPhone) {
          setPhone(propPhone);
        } else {
          const u = auth.currentUser;
          if (u) {
            const snap = await getDoc(doc(db, 'users', u.uid));
            if (snap.exists()) {
              const p = snap.data().phoneNumber || '';
              if (p) setPhone(p);
            } else {
              const local = localStorage.getItem('currentUserProfile');
              if (local) {
                const parsed = JSON.parse(local);
                if (parsed?.phoneNumber) setPhone(parsed.phoneNumber);
              }
            }
          }
        }
      } catch (_) {}
    };
    init();
  }, [propPhone]);

  useEffect(() => {
    if (!isOpen) return;
    setLinkMode(!!auth.currentUser);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      try {
        verifierRef.current?.clear();
      } catch (_) {}
      verifierRef.current = null;
      renderPromiseRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startResendTimer = () => {
    setResendIn(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!phone) {
      toast.error('Enter phone number first.');
      return false;
    }
    if (resendIn > 0) return false;
    if (sendInFlightRef.current) return false;
    setVerificationBlocked(false);

    const e164 = toE164(phone);
    if (!e164) {
      toast.error('Invalid phone format. For US, use 10 digits (e.g., 9191234567) or +1XXXXXXXXXX.');
      return false;
    }

    try {
      setSending(true);
      sendInFlightRef.current = true;
      if (!verifierRef.current) {
        try {
          verifierRef.current = new RecaptchaVerifier(auth, getRecaptchaContainer(), {
            size: 'normal',
          });
          renderPromiseRef.current = verifierRef.current.render();
        } catch (e) {
          console.error('Failed to initialize RecaptchaVerifier:', e);
          toast.error('Failed to initialize reCAPTCHA. Please reload and try again.');
          return false;
        }
      }
      if (renderPromiseRef.current) {
        try {
          await renderPromiseRef.current;
        } catch (_) {
          toast.error('reCAPTCHA failed to load. Please reload the page and try again.');
          return false;
        }
      }
      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(e164, verifierRef.current);
      setVerificationId(vid || '');
      setAttempts(0);
      toast.success(`Code sent to ${e164.replace(/(\+\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4')}`);
      startResendTimer();
      return true;
    } catch (e) {
      console.error('sendCode error', e);
      console.error('sendCode error details', {
        code: e?.code,
        message: e?.message,
        name: e?.name,
        stack: e?.stack,
      });
      if (String(e?.code).includes('invalid-app-credential')) {
        toast.error(
          'App verification failed. Confirm this host is allowed in Firebase Auth and API key settings, then reload and try again.'
        );
      } else if (String(e?.code).includes('too-many-requests')) {
        toast.error('Too many requests. Please wait a few minutes and try again.');
        startResendTimer();
      } else {
        toast.error(e?.message || 'Failed to send code.');
      }
      return false;
    } finally {
      sendInFlightRef.current = false;
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!code || verificationBlocked) return;
    if (attempts >= 3) {
      toast.error('Too many attempts. Please wait a few minutes and try again.');
      return;
    }
    try {
      const e164 = toE164(phone);

      if (!verificationId) {
        toast.error('Verification session expired. Please resend the code.');
        return;
      }

      if (!auth.currentUser) {
        throw new Error(
          'Invariant violation: phone verification attempted without an authenticated email user.'
        );
      }

      const credential = PhoneAuthProvider.credential(verificationId, code);

      if (linkMode && auth.currentUser) {
        await linkWithCredential(auth.currentUser, credential);
        try {
          await updateDoc(doc(db, 'users', auth.currentUser.uid), {
            phoneVerified: true,
            phoneNumber: e164 || phone,
          });
          syncStoredPhoneVerification(e164 || phone);
        } catch (e) {
          console.warn('Failed to update phoneVerified flag:', e);
        }
        toast.success('Phone verified!');
        onVerified?.();
      }
    } catch (e) {
      console.error('confirm error', e);
      if (e?.message?.includes('Invariant violation')) {
        toast.error(
          'Phone verification requires an authenticated email user. Please restart registration.'
        );
        return;
      }
      if (e.code === 'auth/account-exists-with-different-credential') {
        toast.error(
          'This phone number is already registered. Please log in or use a different number.'
        );
        setVerificationBlocked(true);
        return;
      }

      if (e.code === 'auth/code-expired') {
        toast.error('The verification code is no longer valid. Please request a new one.');
        return;
      }

      const errCode = e?.code || '';
      const msg = String(e?.message || '').toLowerCase();

      if (
        errCode === 'auth/invalid-verification-code' ||
        msg.includes('invalid verification code')
      ) {
        setAttempts((a) => a + 1);
        setVerificationId('');
        setCode('');
        toast.error('Wrong code. You can request a new one after the timer.');
      } else if (
        errCode === 'auth/code-expired' ||
        msg.includes('code has expired') ||
        msg.includes('expired')
      ) {
        setVerificationId('');
        setCode('');
        toast.error('Code expired. Please request a new one.');
      } else if (
        errCode === 'auth/too-many-requests' ||
        msg.includes('too many requests')
      ) {
        toast.error('Too many attempts. Please wait a few minutes and try again.');
      } else {
        toast.error('Invalid code. Try again.');
      }
    }
  };

  return (
    <div className="p-4 rounded-xl bg-white shadow w-full max-w-md">
      <h3 className="text-lg font-semibold mb-2">Verify your phone</h3>
      <p className="text-sm text-gray-600 mb-2">
        We will send a one-time code to <b>{phone || 'your phone number'}</b>.
      </p>

      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Phone number"
        readOnly={!!propPhone}
        className="input w-full mb-3"
      />
      <div className="flex gap-2 mb-3">
        <button
          disabled={sending || resendIn > 0}
          onClick={sendCode}
          className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : (sending ? 'Sending' : 'Send code')}
        </button>
        <button onClick={onCancel} className="px-3 py-2 rounded border">
          Cancel
        </button>
      </div>

      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="6-digit code"
        className="input w-full mb-3"
      />
      <button
        onClick={verifyCode}
        disabled={verificationBlocked || !verificationId || !code}
        className="px-3 py-2 rounded bg-green-600 text-white disabled:opacity-50"
      >
        Verify
      </button>
    </div>
  );
}

function NativePhoneVerify({ phone: propPhone, onVerified, onCancel, isOpen = false }) {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [phone, setPhone] = useState(propPhone || '');
  const timerRef = useRef(null);
  const createNativeAuthToken = httpsCallable(
    getFunctions(),
    'createNativeAuthToken'
  );

  useEffect(() => {
    const init = async () => {
      try {
        if (propPhone) {
          setPhone(propPhone);
        } else {
          const u = auth.currentUser;
          if (u) {
            const snap = await getDoc(doc(db, 'users', u.uid));
            if (snap.exists()) {
              const p = snap.data().phoneNumber || '';
              if (p) setPhone(p);
            } else {
              const local = localStorage.getItem('currentUserProfile');
              if (local) {
                const parsed = JSON.parse(local);
                if (parsed?.phoneNumber) setPhone(parsed.phoneNumber);
              }
            }
          }
        }
      } catch (_) {}
    };
    init();
  }, [propPhone]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startResendTimer = () => {
    setResendIn(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!phone) {
      toast.error('Enter phone number first.');
      return false;
    }
    if (resendIn > 0) return false;
    if (sending) return false;

    const e164 = toE164(phone);
    if (!e164) {
      toast.error('Invalid phone format. For US, use 10 digits (e.g., 9191234567) or +1XXXXXXXXXX.');
      return false;
    }

    try {
      setSending(true);
      const result = await startNativePhoneVerification(e164);
      const vid = result?.verificationId || result?.verificationID || '';
      if (!vid) {
        toast.error('Failed to start phone verification on this device.');
        return false;
      }
      setVerificationId(vid);
      setAttempts(0);
      toast.success(`Code sent to ${e164.replace(/(\+\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4')}`);
      startResendTimer();
      return true;
    } catch (e) {
      console.error('native sendCode error', e);
      console.error('native sendCode error details', {
        code: e?.code,
        message: e?.message,
        name: e?.name,
        stack: e?.stack,
        data: e?.data,
      });
      toast.error(e?.message || 'Failed to send code.');
      return false;
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!code) return;

    if (!verificationId) {
      toast.error('Verification session expired. Please resend the code.');
      return;
    }
    if (attempts >= 3) {
      toast.error('Too many attempts. Please wait a few minutes and try again.');
      return;
    }

    try {
      const e164 = toE164(phone);
      if (!auth.currentUser) {
        throw new Error(
          'Invariant violation: phone verification attempted without an authenticated email user.'
        );
      }

      const tokenResult = await createNativeAuthToken();
      const nativeAuthToken = tokenResult?.data?.token || '';
      const expectedUid = tokenResult?.data?.uid || auth.currentUser.uid;
      if (!nativeAuthToken || !expectedUid) {
        throw new Error('Unable to prepare native phone verification. Please sign in again.');
      }

      await confirmNativePhoneCode(
        verificationId,
        code,
        nativeAuthToken,
        expectedUid
      );
      const normalizedPhone = e164 || phone;

      try {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          phoneVerified: true,
          phoneNumber: normalizedPhone,
        });
        syncStoredPhoneVerification(normalizedPhone);
      } catch (e) {
        console.warn('Failed to update phoneVerified flag (native):', e);
      }
      toast.success('Phone verified!');
      onVerified?.();
    } catch (e) {
      console.error('native confirm error', e);
      console.error('native confirm error details', {
        code: e?.code,
        message: e?.message,
        name: e?.name,
        stack: e?.stack,
        data: e?.data,
      });
      if (e?.message?.includes('Invariant violation')) {
        toast.error(
          'Phone verification requires an authenticated email user. Please restart registration.'
        );
        return;
      }

      const errCode = String(e?.code || e?.data?.code || '').toLowerCase();
      const msg = String(e?.message || '').toLowerCase();

      if (msg.includes('invalid verification code')) {
        setAttempts((a) => a + 1);
        setVerificationId('');
        setCode('');
        toast.error('Wrong code. You can request a new one after the timer.');
      } else if (
        errCode.includes('invalid-verification-code') ||
        errCode.includes('invalid_verification_code')
      ) {
        setAttempts((a) => a + 1);
        setVerificationId('');
        setCode('');
        toast.error('Wrong code. You can request a new one after the timer.');
      } else if (
        errCode.includes('session-expired') ||
        errCode.includes('session_expired') ||
        msg.includes('code has expired') ||
        msg.includes('expired')
      ) {
        setVerificationId('');
        setCode('');
        toast.error('Code expired. Please request a new one.');
      } else if (
        errCode.includes('credential-already-in-use') ||
        errCode.includes('credential_already_in_use') ||
        msg.includes('credential is already associated') ||
        msg.includes('already linked to another')
      ) {
        setVerificationId('');
        setCode('');
        toast.error('This phone number is already registered. Please log in or use a different number.');
      } else if (
        errCode.includes('provider-already-linked') ||
        errCode.includes('provider_already_linked') ||
        msg.includes('provider has already been linked') ||
        msg.includes('already linked to this account')
      ) {
        toast.success('Phone is already linked to this account.');
        onVerified?.();
      } else if (msg.includes('too many requests') || msg.includes('unusual activity')) {
        toast.error('Too many attempts. Please wait a few minutes and try again.');
      } else {
        toast.error('Invalid code. Try again.');
      }
    }
  };

  return (
    <div className="p-4 rounded-xl bg-white shadow w-full max-w-md">
      <h3 className="text-lg font-semibold mb-2">Verify your phone</h3>
      <p className="text-sm text-gray-600 mb-2">
        We will send a one-time code to <b>{phone || 'your phone number'}</b>.
      </p>

      <input
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Phone number"
        readOnly={!!propPhone}
        className="input w-full mb-3"
      />

      <div className="flex gap-2 mb-3">
        <button
          disabled={sending || resendIn > 0}
          onClick={sendCode}
          className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : (sending ? 'Sending' : 'Send code')}
        </button>
        <button onClick={onCancel} className="px-3 py-2 rounded border">
          Cancel
        </button>
      </div>

      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="6-digit code"
        className="input w-full mb-3"
      />
      <button
        onClick={verifyCode}
        disabled={!verificationId || !code}
        className="px-3 py-2 rounded bg-green-600 text-white disabled:opacity-50"
      >
        Verify
      </button>
    </div>
  );
}

export default function PhoneVerify(props) {
  const navigate = useNavigate();
  const [isResettingRegistration, setIsResettingRegistration] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const isStandalone =
    props.onVerified === undefined &&
    props.onCancel === undefined &&
    props.isOpen === undefined;
  const functions = getFunctions();
  const cleanupIncompleteRegistration = httpsCallable(
    functions,
    'cleanupIncompleteRegistration'
  );
  const submitRegistrationReview = httpsCallable(
    functions,
    'verifyUserIdentityV2'
  );

  useEffect(() => {
    if (!isStandalone) return;

    let cancelled = false;

    const guardRoute = async () => {
      const user = auth.currentUser;
      if (!user) {
        clearCurrentUserProfile();
        navigate('/login', { replace: true });
        return;
      }

      await user.reload();
      const profileData = await loadUserProfile(user.uid);
      if (!profileData) {
        clearCurrentUserProfile();
        toast.error('User profile not found. Please sign in again.');
        navigate('/login', { replace: true });
        return;
      }

      if (cancelled) return;

      const nextRoute = getPostVerificationRoute(user, profileData);
      if (nextRoute === '/verify-email') {
        localStorage.setItem('pendingEmail', user.email || '');
        navigate('/verify-email', { replace: true });
        return;
      }

      if (nextRoute !== '/verify-phone') {
        persistCurrentUserProfile(user.uid, profileData);
        navigate(nextRoute, { replace: true });
      }
    };

    guardRoute().catch((error) => {
      console.error('phone route guard error', error);
      toast.error('Unable to load your registration status.');
      navigate('/login', { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [isStandalone, navigate]);

  if (!isStandalone) {
    if (Capacitor.isNativePlatform()) {
      return <NativePhoneVerify {...props} />;
    }
    return <WebPhoneVerify {...props} />;
  }

  const handleVerified = async () => {
    const user = auth.currentUser;
    if (!user) {
      clearCurrentUserProfile();
      navigate('/login', { replace: true });
      return;
    }

    await submitRegistrationReview();

    const profileData = await loadUserProfile(user.uid);
    if (!profileData) {
      clearCurrentUserProfile();
      toast.error('User profile not found. Please sign in again.');
      navigate('/login', { replace: true });
      return;
    }

    persistCurrentUserProfile(user.uid, profileData);
    navigate(getPostVerificationRoute(user, profileData), { replace: true });
  };

  const handleCancel = async () => {
    try {
      await auth.signOut();
    } catch (_) {}
    clearCurrentUserProfile();
    navigate('/login', { replace: true });
  };

  const handleResetRegistration = async () => {
    if (!isStandalone || isResettingRegistration) return;

    try {
      setIsResettingRegistration(true);
      if (auth.currentUser) {
        await cleanupIncompleteRegistration();
        try {
          await auth.signOut();
        } catch (_) {}
      }
      clearCurrentUserProfile();
      localStorage.removeItem('pendingEmail');
      toast.info('Registration reset. You can start over with the same email and phone.');
      navigate('/register', { replace: true });
    } catch (error) {
      console.error('cleanupIncompleteRegistration error:', error);
      toast.error(error?.message || 'Failed to reset registration.');
    } finally {
      setIsResettingRegistration(false);
      setShowResetConfirmModal(false);
    }
  };

  const content = Capacitor.isNativePlatform()
    ? (
      <NativePhoneVerify
        {...props}
        isOpen
        onVerified={handleVerified}
        onCancel={handleCancel}
      />
    )
    : (
      <WebPhoneVerify
        {...props}
        isOpen
        onVerified={handleVerified}
        onCancel={handleCancel}
      />
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <RegistrationProgress currentStep="phone" className="mb-4" />
        {content}
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-center">
          <p className="text-sm text-red-800">
            Having trouble verifying your phone?
          </p>
          <button
            onClick={() => setShowResetConfirmModal(true)}
            disabled={isResettingRegistration}
            className="mt-3 w-full rounded-lg bg-red-600 px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
          >
            {isResettingRegistration ? 'Clearing registration...' : 'Delete this registration and start over'}
          </button>
        </div>
      </div>
      {showResetConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">
              Delete registration progress?
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Are you sure you want to delete your registration progress and start registration over? This will clear your current registration so you can begin again.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setShowResetConfirmModal(false)}
                disabled={isResettingRegistration}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetRegistration}
                disabled={isResettingRegistration}
                className="flex-1 rounded-lg bg-red-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {isResettingRegistration ? 'Clearing...' : 'Yes, Start Over'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}
